import React, { ErrorInfo } from 'react';
import { useDebug } from '../hooks/useDebug'; // Adjust path if necessary

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // Note: We can't use hooks directly in class components.
  // To use useDebug, ErrorBoundary would need to be a functional component,
  // or logEvent would need to be passed in as a prop from a parent that can use hooks.
  // For simplicity in this extraction, I'll keep it as a class component and log to console.
  // If you need to use the `logEvent` from `useDebug`, this component would need refactoring
  // or `logEvent` passed as a prop.

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You can also log the error to an error reporting service
    console.error("Uncaught error:", error, errorInfo);
    // If you had logEvent available (e.g., via props or context):
    // this.props.logEvent('error-boundary', { message: error.message, componentStack: errorInfo.componentStack }, {}, error);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div>
          <h2>Something went wrong.</h2>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo?.componentStack}
          </details>
          <p>Check the console for more details.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 